import { useState, useMemo } from 'react';

/**
 * Hook personalizado para gerenciar paginação
 * @param {Object} options - Opções de configuração
 * @param {number} options.initialPage - Página inicial (padrão: 1)
 * @param {number} options.itemsPerPage - Itens por página (padrão: 10)
 * @param {number} options.totalItems - Total de itens
 * @returns {Object} Estado e funções de paginação
 */
export const usePagination = ({ 
  initialPage = 1, 
  itemsPerPage = 10, 
  totalItems = 0 
}) => {
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Cálculos derivados
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalItems / itemsPerPage));
  }, [totalItems, itemsPerPage]);

  const startIndex = useMemo(() => {
    return (currentPage - 1) * itemsPerPage;
  }, [currentPage, itemsPerPage]);

  const endIndex = useMemo(() => {
    return Math.min(startIndex + itemsPerPage - 1, totalItems - 1);
  }, [startIndex, itemsPerPage, totalItems]);

  const hasNextPage = useMemo(() => {
    return currentPage < totalPages;
  }, [currentPage, totalPages]);

  const hasPreviousPage = useMemo(() => {
    return currentPage > 1;
  }, [currentPage]);

  // Funções de navegação
  const goToPage = (page) => {
    const targetPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(targetPage);
  };

  const nextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const previousPage = () => {
    if (hasPreviousPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
  };

  // Reset para primeira página quando totalItems muda
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  return {
    // Estado
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    startIndex,
    endIndex,
    hasNextPage,
    hasPreviousPage,
    
    // Funções
    goToPage,
    nextPage,
    previousPage,
    goToFirstPage,
    goToLastPage,
    resetToFirstPage,
    setCurrentPage,
  };
};

export default usePagination;